import { lazyRetry } from "@/lib/lazy-retry";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { OfflineBanner } from "@/components/OfflineBanner";
import { GlobalSyncStatus } from "@/components/GlobalSyncStatus";
import { ConflictResolver } from "@/components/sync/ConflictResolver";
import { OfflinePrewarm } from "@/hooks/useOfflinePrewarm";

import { DesktopNotifier } from "@/components/DesktopNotifier";
import { DesktopUpdater } from "@/components/DesktopUpdater";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { LangProvider } from "@/contexts/LangContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { DensityProvider } from "@/contexts/DensityContext";
import { LoadingOverlayProvider } from "@/contexts/LoadingOverlayContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleRoute from "@/components/RoleRoute";

import PlatformAdminRoute from "@/components/PlatformAdminRoute";
import PwaUpdatePrompt from "@/components/PwaUpdatePrompt";
import PushNotificationRouter from "@/components/PushNotificationRouter";


// Landing/auth: eager (LCP surfaces + first hop). Everything else is lazy.
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
const ThemePreview = lazyRetry(() => import("./pages/ThemePreview"));
const DesignSystem = lazyRetry(() => import("./pages/DesignSystem"));

const AppLanding = lazyRetry(() => import("./components/AppLanding"));
const DoctorQueue = lazyRetry(() => import("./pages/DoctorQueue"));
const Consultation = lazyRetry(() => import("./pages/Consultation"));
const StaffLogin = lazyRetry(() => import("./pages/StaffLogin"));
const ReceptionDashboard = lazyRetry(() => import("./pages/ReceptionDashboard"));


const AppointmentCalendar = lazyRetry(() => import("./pages/AppointmentCalendar"));
const TodayAppointments = lazyRetry(() => import("./pages/TodayAppointments"));
const AppointmentDetail = lazyRetry(() => import("./pages/AppointmentDetail"));

const Patients = lazyRetry(() => import("./pages/Patients"));
const AddPatient = lazyRetry(() => import("./pages/AddPatient"));
const PatientDetail = lazyRetry(() => import("./pages/PatientDetailRoute"));
const PatientRecords = lazyRetry(() => import("./pages/PatientRecordsRoute"));
const PatientImaging = lazyRetry(() => import("./pages/PatientImagingRoute"));
const PatientMedications = lazyRetry(() => import("./pages/PatientMedicationsRoute"));
const MobilePatientHistory = lazyRetry(() => import("./components/mobile/MobilePatientHistory"));
const PrescriptionPreview = lazyRetry(() => import("./pages/PrescriptionPreviewRoute"));
const Prescriptions = lazyRetry(() => import("./pages/Prescriptions"));
const PrescriptionComposer = lazyRetry(() => import("./pages/PrescriptionComposer"));
const Appointments = lazyRetry(() => import("./pages/AppointmentsRoute"));
const SmartScheduling = lazyRetry(() => import("./pages/SmartScheduling"));
const Tasks = lazyRetry(() => import("./pages/Tasks"));
const Research = lazyRetry(() => import("./pages/Research"));
const Analytics = lazyRetry(() => import("./pages/AnalyticsRoute"));
const FinancialPlanner = lazyRetry(() => import("./pages/FinancialPlannerRoute"));
const FinancialSync = lazyRetry(() => import("./pages/FinancialSyncRoute"));
const MedicoLegal = lazyRetry(() => import("./pages/MedicoLegalRoute"));
const SettingsPage = lazyRetry(() => import("./pages/SettingsPage"));
const PrescriptionTemplatesSettings = lazyRetry(() => import("./pages/PrescriptionTemplatesSettings"));
const Templates = lazyRetry(() => import("./pages/Templates"));
const Chambers = lazyRetry(() => import("./pages/ChambersRoute"));
const Billing = lazyRetry(() => import("./pages/BillingRoute"));
const Medicines = lazyRetry(() => import("./pages/Medicines"));
const MyLibrary = lazyRetry(() => import("./pages/MyLibrary"));
const Inventory = lazyRetry(() => import("./pages/Inventory"));
const Staff = lazyRetry(() => import("./pages/StaffRoute"));
const StaffPortal = lazyRetry(() => import("./pages/StaffPortalRoute"));
const Communications = lazyRetry(() => import("./pages/Communications"));
const LabOrders = lazyRetry(() => import("./pages/LabOrders"));
const LabReferralsRoute = lazyRetry(() => import("./pages/LabReferralsRoute"));
const FamilyHealthRoute = lazyRetry(() => import("./pages/FamilyHealthRoute"));
const EmergencyTriageRoute = lazyRetry(() => import("./pages/EmergencyTriageRoute"));
const DigitalSerialRoute = lazyRetry(() => import("./pages/DigitalSerialRoute"));
const DailyRoundsRoute = lazyRetry(() => import("./pages/DailyRoundsRoute"));
const CommunityHealthRoute = lazyRetry(() => import("./pages/CommunityHealthRoute"));
const CmeTrackerRoute = lazyRetry(() => import("./pages/CmeTrackerRoute"));
const BloodBankRoute = lazyRetry(() => import("./pages/BloodBankRoute"));
const FinancialOpsRoute = lazyRetry(() => import("./pages/FinancialOpsRoute"));
const Vaccinations = lazyRetry(() => import("./pages/Vaccinations"));
const Referrals = lazyRetry(() => import("./pages/ReferralsRoute"));
const DailyIncome = lazyRetry(() => import("./pages/DailyIncome"));
const PracticeDesk = lazyRetry(() => import("./pages/PracticeDesk"));
const DoctorSchedules = lazyRetry(() => import("./pages/DoctorSchedules"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const SharedPrescription = lazyRetry(() => import("./pages/SharedPrescription"));
const WaitingQueue = lazyRetry(() => import("./pages/WaitingQueue"));
const Queue = lazyRetry(() => import("./pages/Queue"));
const ReceptionQueue = lazyRetry(() => import("./pages/ReceptionQueue"));
const PatientCheckin = lazyRetry(() => import("./pages/PatientCheckin"));
const StaffProfile = lazyRetry(() => import("./pages/StaffProfile"));
const KioskCheckin = lazyRetry(() => import("./pages/KioskCheckin"));
const PatientPortal = lazyRetry(() => import("./pages/PatientPortal"));
const ClinicalGuidelinesPage = lazyRetry(() => import("./pages/ClinicalGuidelinesPage"));
const PatientFeedback = lazyRetry(() => import("./pages/PatientFeedbackRoute"));
const ChamberInsights = lazyRetry(() => import("./pages/ChamberInsights"));
const Recalls = lazyRetry(() => import("./pages/Recalls"));
const PublicProfileSettings = lazyRetry(() => import("./pages/PublicProfileSettings"));
const Articles = lazyRetry(() => import("./pages/Articles"));
const Reviews = lazyRetry(() => import("./pages/Reviews"));
const WebsiteOverview = lazyRetry(() => import("./pages/website/WebsiteOverview"));
const WebsiteTemplates = lazyRetry(() => import("./pages/website/WebsiteTemplates"));
const WebsiteContent = lazyRetry(() => import("./pages/website/WebsiteContent"));
const WebsiteBrand = lazyRetry(() => import("./pages/website/WebsiteBrand"));
const WebsiteDomain = lazyRetry(() => import("./pages/website/WebsiteDomain"));
const WebsiteInbox = lazyRetry(() => import("./pages/website/WebsiteInbox"));
const PublicDisplay = lazyRetry(() => import("./pages/PublicDisplay"));
const DisplayNoticesSettings = lazyRetry(() => import("./pages/DisplayNoticesSettings"));
const RoleManagement = lazyRetry(() => import("./pages/RoleManagement"));
const AccountSecurity = lazyRetry(() => import("./pages/AccountSecurityRoute"));
const PlatformAdmin = lazyRetry(() => import("./pages/PlatformAdmin"));
const DisplayControl = lazyRetry(() => import("./pages/DisplayControlRoute"));
const MobileWaitingRoom = lazyRetry(() => import("./components/mobile/MobileWaitingRoom"));
const MobileEngagementHub = lazyRetry(() => import("./components/mobile/MobileEngagementHub"));
const MobileOfflineSync = lazyRetry(() => import("./components/mobile/MobileOfflineSync"));
const MobileQueueHistory = lazyRetry(() => import("./components/mobile/MobileQueueHistory"));
const MobileRemoteMonitoring = lazyRetry(() => import("./components/mobile/MobileRemoteMonitoring"));
const MobileInbox = lazyRetry(() => import("./components/mobile/MobileInbox"));
const MobileVoiceMemos = lazyRetry(() => import("./components/mobile/MobileVoiceMemos"));


const DisplaySimulator = lazyRetry(() => import("./pages/DisplaySimulator"));
const Teleconsultation = lazyRetry(() => import("./pages/TeleconsultationRoute"));
const AuditLog = lazyRetry(() => import("./pages/AuditLog"));
const PatientAccessReport = lazyRetry(() => import("./pages/PatientAccessReport"));
const SubscriptionBilling = lazyRetry(() => import("./pages/SubscriptionBilling"));
const DataExport = lazyRetry(() => import("./pages/DataExport"));
const About = lazyRetry(() => import("./pages/About"));
const Security = lazyRetry(() => import("./pages/Security"));
const PublicDoctorProfile = lazyRetry(() => import("./pages/public/PublicDoctorProfile"));
const TemplatePreview = lazyRetry(() => import("./pages/preview/TemplatePreview"));
const PublicArticle = lazyRetry(() => import("./pages/public/PublicArticle"));
const FeaturePage = lazyRetry(() => import("./pages/public/FeaturePage"));
const AiModulePage = lazyRetry(() => import("./pages/public/AiModulePage"));
const FeaturesPage = lazyRetry(() => import("./pages/FeaturesPage"));
const StoriesPage = lazyRetry(() => import("./pages/StoriesPage"));
const PricingPage = lazyRetry(() => import("./pages/PricingPage"));
const FaqPage = lazyRetry(() => import("./pages/FaqPage"));
const NotificationSettings = lazyRetry(() => import("./pages/NotificationSettings"));
const NotificationPreferences = lazyRetry(() => import("./pages/NotificationPreferences"));
const VoiceCommandsSettings = lazyRetry(() => import("./pages/VoiceCommandsSettings"));
const OfflineSettings = lazyRetry(() => import("./pages/OfflineSettings"));
const SyncAuditLog = lazyRetry(() => import("./pages/SyncAuditLog"));
const NotificationCenter = lazyRetry(() => import("./pages/NotificationCenter"));
const DeviceManagement = lazyRetry(() => import("./pages/DeviceManagement"));

const AmbientScribe = lazyRetry(() => import("./pages/AmbientScribe"));
const AiAgent = lazyRetry(() => import("./pages/AiAgent"));
const AiAgentInbox = lazyRetry(() => import("./pages/AiAgentInbox"));
const Acquisition = lazyRetry(() => import("./pages/Acquisition"));
const CareGaps = lazyRetry(() => import("./pages/CareGaps"));
const ChronicCare = lazyRetry(() => import("./pages/ChronicCare"));
const EmergencyCardPublic = lazyRetry(() => import("./pages/EmergencyCardPublic"));
const Waitlist = lazyRetry(() => import("./pages/Waitlist"));
const Campaigns = lazyRetry(() => import("./pages/Campaigns"));
const Reports = lazyRetry(() => import("./pages/Reports"));
const ClaimRules = lazyRetry(() => import("./pages/ClaimRules"));
const NpsDashboard = lazyRetry(() => import("./pages/NpsDashboard"));
const NpsSurveyPublic = lazyRetry(() => import("./pages/NpsSurveyPublic"));
const SpecialtyTemplates = lazyRetry(() => import("./pages/SpecialtyTemplates"));
const CalendarSync = lazyRetry(() => import("./pages/CalendarSync"));
const ReviewsInbox = lazyRetry(() => import("./pages/ReviewsInbox"));
const Rounds = lazyRetry(() => import("./pages/Rounds"));
import GlobalFab from "./components/GlobalFab";
import DemoSimulator from "./components/DemoSimulator";

import { PresenceProvider } from "@/hooks/usePresence";

// queryClient is created in src/lib/query-client.ts with IndexedDB persistence

/* No blocking preloader: chunks usually resolve in a few ms, so we render
   nothing at first and only fade in a slim top bar if the load is genuinely
   slow. Prevents the spinner flash that made navigation feel heavy. */
const RouteFallback = () => (
  <div aria-hidden className="route-progress" />
);

/** Warm the chunks doctors hit most, once the browser is idle. */
const prefetchHotRoutes = () => {
  const load = () => {
    import("./pages/Index");
    import("./pages/Patients");
    import("./pages/Prescriptions");
    import("./pages/PrescriptionComposer");
    import("./pages/AppointmentsRoute");
  };
  if (typeof window === "undefined") return;
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(load, { timeout: 3000 });
  } else {
    setTimeout(load, 1500);
  }
};

prefetchHotRoutes();


const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LangProvider>
      <ThemeProvider>
      <DensityProvider>
      <TooltipProvider>
      <LoadingOverlayProvider>

        <Toaster />
        <Sonner />
        <PwaUpdatePrompt />
        <OfflineBanner />
        <GlobalSyncStatus />
        <ConflictResolver />
        <OfflinePrewarm />

        <BrowserRouter>
          <PresenceProvider>
          <DesktopNotifier />
          <PushNotificationRouter />


          <DesktopUpdater />
          <GlobalFab />
          <DemoSimulator />

          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Marketing site at root */}
            <Route path="/" element={<Landing />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/theme-preview" element={<ThemePreview />} />
            <Route path="/design-system" element={<DesignSystem />} />
            <Route path="/about" element={<About />} />
            <Route path="/security" element={<Security />} />
            <Route path="/features/:slug" element={<FeaturePage section="features" />} />
            <Route path="/solutions/:slug" element={<FeaturePage section="solutions" />} />
            <Route path="/resources/:slug" element={<FeaturePage section="resources" />} />
            <Route path="/ai/:slug" element={<AiModulePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/stories" element={<StoriesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FaqPage />} />

            <Route path="/auth" element={<Auth />} />
            <Route path="/rx/:token" element={<SharedPrescription />} />
            <Route path="/waiting-queue" element={<WaitingQueue />} />
            <Route path="/kiosk" element={<KioskCheckin />} />
            <Route path="/patient-portal" element={<PatientPortal />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/display/:chamberId" element={<PublicDisplay />} />
            <Route path="/dr/:slug" element={<PublicDoctorProfile />} />
            <Route path="/dr/:slug/article/:articleSlug" element={<PublicArticle />} />
            <Route path="/nps/:token" element={<NpsSurveyPublic />} />
            <Route path="/e/:token" element={<EmergencyCardPublic />} />
            <Route path="/preview/template/:preset" element={<TemplatePreview />} />

            <Route path="/public-profile" element={<ProtectedRoute><PublicProfileSettings /></ProtectedRoute>} />
            <Route path="/articles" element={<ProtectedRoute><Articles /></ProtectedRoute>} />
            <Route path="/reviews" element={<ProtectedRoute><Reviews /></ProtectedRoute>} />

            {/* App now lives under /app */}
            <Route path="/app" element={<ProtectedRoute><AppLanding /></ProtectedRoute>} />
            {/* Clinic-wide queue board: front desk + admins only */}
            <Route
              path="/queue"
              element={
                <RoleRoute allow={["admin", "receptionist", "platform_admin"]}>
                  <Queue />
                </RoleRoute>
              }
            />

            <Route path="/staff-login" element={<StaffLogin />} />
            <Route
              path="/reception"
              element={
                <RoleRoute allow={["receptionist", "admin"]} loginPath="/staff-login">
                  <ReceptionDashboard />
                </RoleRoute>
              }
            />
            <Route
              path="/reception-queue"
              element={
                <RoleRoute allow={["receptionist", "admin"]} loginPath="/staff-login">
                  <ReceptionQueue />
                </RoleRoute>
              }
            />
            <Route
              path="/patient-checkin"
              element={
                <RoleRoute allow={["receptionist", "admin"]} loginPath="/staff-login">
                  <PatientCheckin />
                </RoleRoute>
              }
            />
            <Route
              path="/staff-profile"
              element={
                <RoleRoute allow={["receptionist", "admin", "staff"]} loginPath="/staff-login">
                  <StaffProfile />
                </RoleRoute>
              }
            />



            {/* Doctor-facing queue + consultation: clinicians (and account owners) only */}
            <Route
              path="/doctor-queue"
              element={
                <RoleRoute allow={["doctor", "admin", "platform_admin"]}>
                  <DoctorQueue />
                </RoleRoute>
              }
            />
            <Route
              path="/consultation"
              element={
                <RoleRoute allow={["doctor", "admin", "platform_admin"]}>
                  <Consultation />
                </RoleRoute>
              }
            />



            <Route path="/patients" element={<ProtectedRoute><Patients /></ProtectedRoute>} />
            <Route path="/patients/new" element={<ProtectedRoute><AddPatient /></ProtectedRoute>} />
            <Route path="/patients/:id" element={<ProtectedRoute><PatientDetail /></ProtectedRoute>} />
            <Route path="/patients/:id/records" element={<ProtectedRoute><PatientRecords /></ProtectedRoute>} />
            <Route path="/patients/:id/imaging" element={<ProtectedRoute><PatientImaging /></ProtectedRoute>} />
            <Route path="/patients/:id/medications" element={<ProtectedRoute><PatientMedications /></ProtectedRoute>} />
            <Route path="/patients/:id/history" element={<ProtectedRoute><MobilePatientHistory /></ProtectedRoute>} />
            <Route path="/prescriptions" element={<ProtectedRoute><Prescriptions /></ProtectedRoute>} />
            <Route path="/prescriptions/new" element={<ProtectedRoute><PrescriptionComposer /></ProtectedRoute>} />
            <Route path="/prescriptions/:id" element={<ProtectedRoute><PrescriptionPreview /></ProtectedRoute>} />
            <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
            <Route path="/appointments/calendar" element={<ProtectedRoute><AppointmentCalendar /></ProtectedRoute>} />
            <Route path="/appointments/today" element={<ProtectedRoute><TodayAppointments /></ProtectedRoute>} />
            <Route path="/appointments/:id" element={<ProtectedRoute><AppointmentDetail /></ProtectedRoute>} />

            <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/research" element={<ProtectedRoute><Research /></ProtectedRoute>} />
            <Route path="/smart-scheduling" element={<ProtectedRoute><SmartScheduling /></ProtectedRoute>} />
            <Route path="/teleconsultation" element={<ProtectedRoute><Teleconsultation /></ProtectedRoute>} />
            <Route path="/chambers" element={<ProtectedRoute><Chambers /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/medicines" element={<ProtectedRoute><Medicines /></ProtectedRoute>} />
            <Route path="/my-library" element={<ProtectedRoute><MyLibrary /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
            <Route path="/staff" element={<ProtectedRoute><Staff /></ProtectedRoute>} />
            <Route path="/staff-portal" element={<ProtectedRoute><StaffPortal /></ProtectedRoute>} />
            <Route path="/communications" element={<ProtectedRoute><Communications /></ProtectedRoute>} />
            <Route path="/lab-orders" element={<ProtectedRoute><LabOrders /></ProtectedRoute>} />
            <Route path="/blood-bank" element={<ProtectedRoute><BloodBankRoute /></ProtectedRoute>} />
            <Route path="/cme" element={<ProtectedRoute><CmeTrackerRoute /></ProtectedRoute>} />
            <Route path="/community-health" element={<ProtectedRoute><CommunityHealthRoute /></ProtectedRoute>} />
            <Route path="/daily-rounds" element={<ProtectedRoute><DailyRoundsRoute /></ProtectedRoute>} />
            <Route path="/digital-serial" element={<ProtectedRoute><DigitalSerialRoute /></ProtectedRoute>} />
            <Route path="/emergency-triage" element={<ProtectedRoute><EmergencyTriageRoute /></ProtectedRoute>} />
            <Route path="/family-health" element={<ProtectedRoute><FamilyHealthRoute /></ProtectedRoute>} />
            <Route path="/lab-referrals" element={<ProtectedRoute><LabReferralsRoute /></ProtectedRoute>} />
            <Route path="/financial-operations" element={<ProtectedRoute><FinancialOpsRoute /></ProtectedRoute>} />
            <Route path="/vaccinations" element={<ProtectedRoute><Vaccinations /></ProtectedRoute>} />
            <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
            <Route path="/daily-income" element={<ProtectedRoute><DailyIncome /></ProtectedRoute>} />
            <Route path="/practice-desk" element={<ProtectedRoute><PracticeDesk /></ProtectedRoute>} />
            <Route path="/schedule" element={<ProtectedRoute><DoctorSchedules /></ProtectedRoute>} />
            <Route path="/clinical-guidelines" element={<ProtectedRoute><ClinicalGuidelinesPage /></ProtectedRoute>} />
            <Route path="/patient-feedback" element={<ProtectedRoute><PatientFeedback /></ProtectedRoute>} />
            <Route path="/chamber-insights" element={<ProtectedRoute><ChamberInsights /></ProtectedRoute>} />
            <Route path="/recalls" element={<ProtectedRoute><Recalls /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/financial-planner" element={<ProtectedRoute><FinancialPlanner /></ProtectedRoute>} />
            <Route path="/financial-sync" element={<ProtectedRoute><FinancialSync /></ProtectedRoute>} />
            <Route path="/medico-legal" element={<ProtectedRoute><MedicoLegal /></ProtectedRoute>} />
            <Route path="/website" element={<ProtectedRoute><WebsiteOverview /></ProtectedRoute>} />
            <Route path="/website/templates" element={<ProtectedRoute><WebsiteTemplates /></ProtectedRoute>} />
            <Route path="/website/content" element={<ProtectedRoute><WebsiteContent /></ProtectedRoute>} />
            <Route path="/website/brand" element={<ProtectedRoute><WebsiteBrand /></ProtectedRoute>} />
            <Route path="/website/domain" element={<ProtectedRoute><WebsiteDomain /></ProtectedRoute>} />
            <Route path="/website/inbox" element={<ProtectedRoute><WebsiteInbox /></ProtectedRoute>} />
            <Route path="/scribe" element={<ProtectedRoute><AmbientScribe /></ProtectedRoute>} />
            <Route path="/rounds" element={<ProtectedRoute><Rounds /></ProtectedRoute>} />
            <Route path="/ai-agent" element={<ProtectedRoute><AiAgent /></ProtectedRoute>} />
            <Route path="/ai-agent/inbox" element={<ProtectedRoute><AiAgentInbox /></ProtectedRoute>} />
            <Route path="/acquisition" element={<ProtectedRoute><Acquisition /></ProtectedRoute>} />
            <Route path="/care-gaps" element={<ProtectedRoute><CareGaps /></ProtectedRoute>} />
            <Route path="/chronic-care" element={<ProtectedRoute><ChronicCare /></ProtectedRoute>} />
            <Route path="/waitlist" element={<ProtectedRoute><Waitlist /></ProtectedRoute>} />
            <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/nps" element={<ProtectedRoute><NpsDashboard /></ProtectedRoute>} />
            <Route path="/specialty-templates" element={<ProtectedRoute><SpecialtyTemplates /></ProtectedRoute>} />
            <Route path="/reviews-inbox" element={<ProtectedRoute><ReviewsInbox /></ProtectedRoute>} />
            <Route path="/settings/calendar-sync" element={<ProtectedRoute><CalendarSync /></ProtectedRoute>} />
            <Route path="/settings/claim-rules" element={<ProtectedRoute><ClaimRules /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/settings/voice-commands" element={<ProtectedRoute><VoiceCommandsSettings /></ProtectedRoute>} />
            <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
            <Route path="/settings/offline" element={<ProtectedRoute><OfflineSettings /></ProtectedRoute>} />
            <Route path="/settings/sync-log" element={<ProtectedRoute><SyncAuditLog /></ProtectedRoute>} />
            <Route path="/settings/notification-preferences" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />
            <Route path="/settings/devices" element={<ProtectedRoute><DeviceManagement /></ProtectedRoute>} />


            <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
            <Route path="/settings/prescription-templates" element={<ProtectedRoute><PrescriptionTemplatesSettings /></ProtectedRoute>} />
            <Route path="/settings/display" element={<ProtectedRoute><DisplayNoticesSettings /></ProtectedRoute>} />
            <Route path="/display-control" element={<ProtectedRoute><DisplayControl /></ProtectedRoute>} />
            <Route path="/waiting-room" element={<ProtectedRoute><MobileWaitingRoom /></ProtectedRoute>} />
            <Route path="/engagement" element={<ProtectedRoute><MobileEngagementHub /></ProtectedRoute>} />
            <Route path="/offline-sync" element={<ProtectedRoute><MobileOfflineSync /></ProtectedRoute>} />
            <Route path="/queue-history" element={<ProtectedRoute><MobileQueueHistory /></ProtectedRoute>} />
            <Route path="/remote-monitoring" element={<ProtectedRoute><MobileRemoteMonitoring /></ProtectedRoute>} />
            <Route path="/voice-memos" element={<ProtectedRoute><MobileVoiceMemos /></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute><MobileInbox /></ProtectedRoute>} />
            <Route path="/inbox/:threadKey" element={<ProtectedRoute><MobileInbox /></ProtectedRoute>} />


            <Route path="/display-simulator" element={<ProtectedRoute><DisplaySimulator /></ProtectedRoute>} />
            <Route path="/settings/roles" element={<ProtectedRoute><RoleManagement /></ProtectedRoute>} />
            <Route path="/account-security" element={<ProtectedRoute><AccountSecurity /></ProtectedRoute>} />
            <Route path="/platform-admin" element={<PlatformAdminRoute><PlatformAdmin /></PlatformAdminRoute>} />
            <Route path="/platform-admin/*" element={<PlatformAdminRoute><PlatformAdmin /></PlatformAdminRoute>} />
            <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
            <Route path="/access-report" element={<ProtectedRoute><PatientAccessReport /></ProtectedRoute>} />
            <Route path="/subscription" element={<ProtectedRoute><SubscriptionBilling /></ProtectedRoute>} />

            <Route path="/settings/data-export" element={<ProtectedRoute><DataExport /></ProtectedRoute>} />
            {/* Legacy / alternate URLs — redirect instead of 404 */}
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />
            <Route path="/home" element={<Navigate to="/app" replace />} />
            <Route path="/add-patient" element={<Navigate to="/patients/new" replace />} />
            <Route path="/new-patient" element={<Navigate to="/patients/new" replace />} />
            <Route path="/new-prescription" element={<Navigate to="/prescriptions/new" replace />} />
            <Route path="/doctor-schedules" element={<Navigate to="/schedule" replace />} />
            <Route path="/schedules" element={<Navigate to="/schedule" replace />} />
            <Route path="/kiosk-checkin" element={<Navigate to="/kiosk" replace />} />
            <Route path="/today-appointments" element={<Navigate to="/appointments/today" replace />} />
            <Route path="/appointment-calendar" element={<Navigate to="/appointments/calendar" replace />} />
            <Route path="/calendar" element={<Navigate to="/appointments/calendar" replace />} />
            <Route path="/ai-agent-inbox" element={<Navigate to="/ai-agent/inbox" replace />} />
            <Route path="/ai-inbox" element={<Navigate to="/ai-agent/inbox" replace />} />
            <Route path="/notification-preferences" element={<Navigate to="/settings/notifications" replace />} />
            <Route path="/data-export" element={<Navigate to="/settings/data-export" replace />} />
            <Route path="/roles" element={<Navigate to="/settings/roles" replace />} />
            <Route path="/calendar-sync" element={<Navigate to="/settings/calendar-sync" replace />} />
            <Route path="/labs" element={<Navigate to="/lab-orders" replace />} />
            <Route path="/income" element={<Navigate to="/daily-income" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </PresenceProvider>
        </BrowserRouter>
      </LoadingOverlayProvider>
      </TooltipProvider>
      </DensityProvider>
      </ThemeProvider>
      </LangProvider>
    </AuthProvider>
  </QueryClientProvider>
);


export default App;
